export const QUERY_TIME = {
    FIVE_MINUTES: 5 * 60 * 1000,
    THIRTY_SECONDS: 30_000,
    NEVER: Number.POSITIVE_INFINITY,
} as const;

export const QUERY_CONFIG = {
    POSITIONS: {
        staleTime: QUERY_TIME.THIRTY_SECONDS,
        gcTime: QUERY_TIME.FIVE_MINUTES,
    },
    TOKENS: {
        staleTime: QUERY_TIME.NEVER,
        gcTime: QUERY_TIME.FIVE_MINUTES,
    },
    POINTS: {
        staleTime: QUERY_TIME.NEVER,
        gcTime: QUERY_TIME.FIVE_MINUTES,
    },
    UNWINDING: {
        staleTime: QUERY_TIME.THIRTY_SECONDS,
        gcTime: QUERY_TIME.FIVE_MINUTES,
    },
    VOTES: {
        staleTime: QUERY_TIME.THIRTY_SECONDS,
        gcTime: QUERY_TIME.FIVE_MINUTES,
    },
    PROTOCOL: {
        staleTime: QUERY_TIME.THIRTY_SECONDS,
        gcTime: QUERY_TIME.FIVE_MINUTES,
    },
    REDEMPTIONS: {
        staleTime: QUERY_TIME.THIRTY_SECONDS,
        gcTime: QUERY_TIME.FIVE_MINUTES,
    },
} as const;

export const GAS_PRICE_CACHE_KEY = "gasPrice";
export const GAS_PRICE_REFRESH_INTERVAL = 15000; // 15 seconds

const POINTS_QUERY_KEY = "POINTS";
const PVE_QUEST_QUERY_KEY = `${POINTS_QUERY_KEY}:pve-quest`;
const USER_POINTS_QUERY_KEY = `${POINTS_QUERY_KEY}:user`;

const TOKENS_QUERY_KEY = "TOKEN_BALANCE";
const POSITIONS_QUERY_KEY = "POSITIONS";
const USER_QUERY_KEY = "USER";
const UNWINDING_QUERY_KEY = "UNWINDING";
const PROTOCOL_QUERY_KEY = "PROTOCOL";
const REDEMPTIONS_QUERY_KEY = "REDEMPTIONS";

const LOCK_QUERY_KEY = `${POSITIONS_QUERY_KEY}:lock`;
const STAKE_QUERY_KEY = `${POSITIONS_QUERY_KEY}:stake`;
const USER_UNWINDING_QUERY_KEY = `${UNWINDING_QUERY_KEY}:user`;
const USER_REDEMPTIONS_QUERY_KEY = `${REDEMPTIONS_QUERY_KEY}:user`;

const TOKENS_BALANCE_QUERY_KEY = `${TOKENS_QUERY_KEY}:balance`;
const USER_PROFILE_QUERY_KEY = `${USER_QUERY_KEY}:profile`;
const USER_WALLET_QUERY_KEY = `${USER_QUERY_KEY}:wallet`;
const ENS_NAME_QUERY_KEY = `${USER_QUERY_KEY}:ens-name`;

const VOTES_QUERY_KEY = "VOTES";
const GLOBAL_VOTES_QUERY_KEY = `${VOTES_QUERY_KEY}:global`;
const PROTOCOL_DATA_QUERY_KEY = `${PROTOCOL_QUERY_KEY}:data`;
const USER_HISTORY_QUERY_KEY = `${PROTOCOL_QUERY_KEY}:user-history`;

const USER_VOTES_QUERY_KEY = `${VOTES_QUERY_KEY}:user`;

const ANNOUNCEMENT_QUERY_KEY = "ANNOUNCEMENT";
const ANNOUNCEMENT_USER_QUERY_KEY = `${ANNOUNCEMENT_QUERY_KEY}:user`;

export const QUERY_KEYS = {
    POSITIONS: {
        LOCK: {
            LIST: (address?: string) => (address ? [LOCK_QUERY_KEY, address] : [LOCK_QUERY_KEY]),
        },
        STAKE: (address?: string) => (address ? [STAKE_QUERY_KEY, address] : [STAKE_QUERY_KEY]),
    },
    TOKENS: {
        BALANCE: {
            LIST: (address?: string) => (address ? [TOKENS_BALANCE_QUERY_KEY, address] : [TOKENS_BALANCE_QUERY_KEY]),
        },
    },
    POINTS: {
        PVE_QUEST: [PVE_QUEST_QUERY_KEY],
        USER: (address?: string) => (address ? [USER_POINTS_QUERY_KEY, address] : [USER_POINTS_QUERY_KEY]),
    },
    USER: {
        PROFILE: (address?: string) => (address ? [USER_PROFILE_QUERY_KEY, address] : [USER_PROFILE_QUERY_KEY]),
        WALLET: (address?: string) => (address ? [USER_WALLET_QUERY_KEY, address] : [USER_WALLET_QUERY_KEY]),
        ENS_NAME: (address?: string) => (address ? [ENS_NAME_QUERY_KEY, address] : [ENS_NAME_QUERY_KEY]),
    },
    UNWINDING: {
        USER: (address?: string) => (address ? [USER_UNWINDING_QUERY_KEY, address] : [USER_UNWINDING_QUERY_KEY]),
    },
    REDEMPTIONS: {
        USER: (address: string) => [USER_REDEMPTIONS_QUERY_KEY, address],
    },
    VOTES: {
        GLOBAL: (epoch?: number) => (epoch !== undefined ? [GLOBAL_VOTES_QUERY_KEY, epoch] : [GLOBAL_VOTES_QUERY_KEY]),
        USER: (epoch: number, address?: string) =>
            address ? [USER_VOTES_QUERY_KEY, epoch, address] : [USER_VOTES_QUERY_KEY, epoch],
    },
    PROTOCOL: {
        DATA: [PROTOCOL_DATA_QUERY_KEY],
        PNL: (address: string) => [PROTOCOL_DATA_QUERY_KEY, address],
        USER_HISTORY: (type: string, address?: string, daysAgo?: number) =>
            address && daysAgo !== undefined
                ? [USER_HISTORY_QUERY_KEY, type, address, daysAgo]
                : [USER_HISTORY_QUERY_KEY, type],
    },
    ANNOUNCEMENT: {
        USER: (address: string) => [ANNOUNCEMENT_USER_QUERY_KEY, address],
    },
} as const;

export type QueryKeys = typeof QUERY_KEYS;
